import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, map, Observable } from "rxjs";
import { ConfigService } from "../config/config.service";
import {
  PatchedCollectionOfDocRef,
  PatchedDefaultDocRef,
} from "../models/patched-default-doc-ref";

@Injectable({
  providedIn: "root",
})
export class PiaProductPicturesService {
  http = inject(HttpClient);
  configService = inject(ConfigService);

  getDocumentRefWithDocumentAndDocumentCategoriesExpansionByCountryYearAndProducts(
    {
      countryID,
      year,
      productIDs,
    }: {
      countryID: string;
      year: number;
      productIDs: string[];
    },
    context?: HttpContext,
  ): Observable<{ d?: PatchedCollectionOfDocRef }> {
    const headers = new HttpHeaders({
      accept: "application/json",
    });

    const docRefRequests = productIDs.map((productID) => {
      const url = `${this.configService.piaAPIOrigin}/DocRef?$filter=Year eq ${encodeURIComponent(year)} and Product eq '${encodeURIComponent(productID)}' and Country eq '${encodeURIComponent(countryID.toUpperCase())}' and Language eq 'de' and Document/CategoryId eq 'image'&$expand=Document`;
      return this.http.get<{ d?: PatchedCollectionOfDocRef }>(url, {
        headers,
        context,
      });
    });

    return combineLatest([...docRefRequests]).pipe(
      map((collectionsOfDocRef: { d?: PatchedCollectionOfDocRef }[]) => {
        return {
          d: {
            results: Array.from(
              new Set([
                ...(collectionsOfDocRef
                  ?.map((collectionOfDocRef) => collectionOfDocRef?.d?.results)
                  .flat() ?? []),
              ]),
            ),
          },
        } as { d?: PatchedCollectionOfDocRef };
      }),
    );
  }

  getDocumentsByCountryLanguageYearAndProduct(
    {
      countryID,
      year,
      productIDs,
      includedLanguages,
    }: {
      countryID: string;
      year: number;
      productIDs: string[];
      includedLanguages: string[];
    },
    context?: HttpContext,
  ) {
    const response =
      this.getDocumentRefWithDocumentAndDocumentCategoriesExpansionByCountryYearAndProducts(
        {
          countryID,
          year,
          productIDs,
        },
        context,
      );

    // const documentIdsIncluded = new Set<string>();

    return response.pipe(
      map((documentReferences: { d?: PatchedCollectionOfDocRef }) => {
        return !documentReferences.d?.results
          ? []
          : documentReferences.d.results.filter(
              (documentReference: PatchedDefaultDocRef) => {
                if (!documentReference.DocumentId) {
                  return false;
                }
                const countryMatches =
                  documentReference?.Country &&
                  // use current country and int (international) document
                  (documentReference?.Country.toLowerCase() ===
                    countryID.toLowerCase() ||
                    documentReference?.Country.toLowerCase() === "int");
                const documentLanguageIncluded =
                  documentReference.Language &&
                  includedLanguages.includes(documentReference.Language);
                return (
                  countryMatches &&
                  documentLanguageIncluded &&
                  this.configService.piaProductImagesTypes.includes(
                    documentReference.Document?.Type as string,
                  ) &&
                  productIDs.includes(documentReference.Product as string)
                );
              },
            );
      }),
    );
  }
}
